import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { TableCell, TableRow, Collapse, Box, Typography } from "@material-ui/core";
import { find, map, findIndex } from "lodash";
import { getOrgUnitsForOrgUnitsSelector, getEndorsementTypeSettings } from "app/main/orgUnits/reducers/orgUnit.reducers";
import { fetchOrgUnitsForSiteSelector, fetchEndorsementTypeConfiguration } from "app/main/orgUnits/actions/orgUnit.actions";
import { getAllRoles, getUserAdminOptionalFields } from "app/auth/store/reducers/system-configuration";
import { getEverySpecialtyProfile, areSpecialtyProfilesLoaded, getSubSpecialties, areSubSpecialtiesLoaded } from "app/main/specialtyProfiles/reducers/specialtyProfiles.reducers";
import { getAllSpecialtyProfiles as fetchAllSpecialtyProfiles, fetchAllSubSpecialties } from "app/main/specialtyProfiles/actions/specialtyProfiles.actions";
import PagingTable from "components/items/paging-table";
import { USER_IMPORT_LOG_PAGE_SIZE_OPTIONS } from "app/auth/store/reducers/userImportLog.reducer";
import { fetchBulkUserImportLogDetails } from "../actions/users.actions";
import { getImportLogDetails,
  getImportLogDetailsPageInfo,
  isImportLogDetailsLoading } from "../reducers/users.reducers";

const useStyles = makeStyles(theme => ({
  changedField: {
    backgroundColor: "#fff3cd",
    padding: "4px 8px",
    borderRadius: "4px",
    marginLeft: "-8px",
    marginRight: "-8px",
  },
  userDataBox: {
    flex: 1,
    padding: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  title: {
    marginRight: theme.spacing(1),
  },
  titleLink: {
    fontSize: "14px",
  },
  fieldBox: {
    marginBottom: theme.spacing(1),
  },
  fieldLabel: {
    fontWeight: "bold",
    display: "inline",
  },
  fieldValue: {
    display: "inline",
  },
  userRolesBox: {
    marginBottom: theme.spacing(1),
  },
  userRolesText: {
    marginLeft: theme.spacing(1),
  },
  comparisonContainer: {
    padding: theme.spacing(2),
  },
  comparisonBoxes: {
    display: "flex",
    gap: theme.spacing(2),
  },
  errorResultBox: {
    flex: 1,
    padding: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
  },
  errorResultTitle: {
    marginBottom: theme.spacing(2),
  },
  expandedRow: {
    cursor: "pointer",
    backgroundColor: "#f5f5f5",
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  normalRow: {
    cursor: "pointer",
    borderLeft: "3px solid transparent",
  },
  expandedCell: {
    fontWeight: "bold",
  },
  expandedErrorCell: {
    fontWeight: "bold",
    color: theme.palette.error.main,
  },
  collapseCell: {
    paddingBottom: 0,
    paddingTop: 0,
  },
}));

const UserImportLogDetailsTable = ({ orgUnitId, importLogId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const [expandedRow, setExpandedRow] = useState(null);
  const [orgUnitsFetched, setOrgUnitsFetched] = useState(false);
  const [endorsementTypesFetched, setEndorsementTypesFetched] = useState(false);
  const [specialtyProfilesFetched, setSpecialtyProfilesFetched] = useState(false);
  const [subSpecialtiesFetched, setSubSpecialtiesFetched] = useState(false);

  const details = useSelector(getImportLogDetails);
  const pageInfo = useSelector(getImportLogDetailsPageInfo);
  const isLoading = useSelector(isImportLogDetailsLoading);
  const roles = useSelector(getAllRoles);
  const orgUnits = useSelector(getOrgUnitsForOrgUnitsSelector);
  const endorsementTypes = useSelector(state => getEndorsementTypeSettings(state, orgUnitId));
  const specialtyProfilesLoaded = useSelector(areSpecialtyProfilesLoaded);
  const specialtyProfiles = useSelector(getEverySpecialtyProfile);
  const subSpecialtiesLoaded = useSelector(areSubSpecialtiesLoaded);
  const subSpecialties = useSelector(getSubSpecialties);
  const userAdminOptionalFields = useSelector(getUserAdminOptionalFields);
  const showSpecialtyProfiles = findIndex(userAdminOptionalFields, x => x === "SpecialtyProfileId") !== -1;

  useEffect(() => {
    if (importLogId && orgUnitId) {
      dispatch(fetchBulkUserImportLogDetails(orgUnitId, importLogId, pageInfo.pageNumber, pageInfo.pageSize));
    }
  }, [dispatch, orgUnitId, importLogId, pageInfo.pageNumber, pageInfo.pageSize]);

  useEffect(() => {
    if (!orgUnitsFetched && (!orgUnits || orgUnits.length === 0)) {
      dispatch(fetchOrgUnitsForSiteSelector());
      setOrgUnitsFetched(true);
    }
  }, [dispatch, orgUnits, orgUnitsFetched]);

  useEffect(() => {
    if (!endorsementTypesFetched && orgUnitId && (!endorsementTypes || endorsementTypes.length === 0)) {
      dispatch(fetchEndorsementTypeConfiguration(orgUnitId));
      setEndorsementTypesFetched(true);
    }
  }, [dispatch, orgUnitId, endorsementTypes, endorsementTypesFetched]);

  useEffect(() => {
    if (!specialtyProfilesFetched && !specialtyProfilesLoaded) {
      dispatch(fetchAllSpecialtyProfiles());
      setSpecialtyProfilesFetched(true);
    }
  }, [dispatch, specialtyProfilesLoaded, specialtyProfilesFetched]);

  useEffect(() => {
    if (!subSpecialtiesFetched && !subSpecialtiesLoaded) {
      dispatch(fetchAllSubSpecialties(true));
      setSubSpecialtiesFetched(true);
    }
  }, [dispatch, subSpecialtiesLoaded, subSpecialtiesFetched]);

  const loadImportLogDetails = (pageNumber, pageSize) => dispatch(fetchBulkUserImportLogDetails(orgUnitId, importLogId, pageNumber, pageSize));

  const onChangeRowsPerPage = pageSize => loadImportLogDetails(1, pageSize);
  const onChangePage = pageNumber => loadImportLogDetails(pageNumber + 1, pageInfo.pageSize);

  const renderTableHeader = () => (
    <TableRow>
      <TableCell>Row Number</TableCell>
      <TableCell>Username</TableCell>
      <TableCell>Update Type</TableCell>
      <TableCell>Other Contributing Rows</TableCell>
      <TableCell>Error Message</TableCell>
    </TableRow>
  );
  const handleRowClick = rowNumber => {
    setExpandedRow(expandedRow === rowNumber ? null : rowNumber);
  };

  const formatUserRoles = userRoles => {
    if (!userRoles || userRoles.length === 0) return "-";
    return map(userRoles, userRole => {
      const role = find(roles, r => r.roleId === userRole.RoleId);
      const orgUnit = find(orgUnits, ou => ou.id === userRole.OrgUnitId);

      const roleDescription = role?.roleDescription || role?.roleName || userRole.RoleId;
      const orgUnitName = orgUnit?.name || userRole.OrgUnitId;
      return `${roleDescription} (${orgUnitName})`;
    }).join(", ");
  };

  const formatEndorsementTypes = userEndorsementTypes => {
    if (!userEndorsementTypes || userEndorsementTypes.length === 0) return "-";

    return map(userEndorsementTypes, endorsementType => {
      const endorsementTypeItem = find(endorsementTypes, et => et.endorsedByType === endorsementType.EndorsedByType);
      return endorsementTypeItem?.endorsedByTypeLabel || endorsementType.EndorsedByType;
    }).join(", ");
  };

  const formatUserSpecialtyProfiles = userSpecialtyProfiles => {
    if (!userSpecialtyProfiles || userSpecialtyProfiles.length === 0) return "-";

    return map(userSpecialtyProfiles, userSpecialty => {
      const specialty = specialtyProfiles?.[userSpecialty.SpecialtyProfileId];
      const subSpecialty = userSpecialty.SubSpecialtyId
        ? find(subSpecialties, ss => ss.id === userSpecialty.SubSpecialtyId)
        : null;
      const orgUnit = find(orgUnits, ou => ou.id === userSpecialty.OrgUnitId);

      const specialtyName = specialty?.displayName || userSpecialty.SpecialtyProfileId;
      const subSpecialtyName = subSpecialty ? ` - ${subSpecialty.displayName}` : "";
      const orgUnitName = orgUnit?.name || userSpecialty.OrgUnitId;

      return `${specialtyName}${subSpecialtyName} (${orgUnitName})`;
    }).join(", ");
  };

  const hasFieldChanged = (previousValue, updatedValue) => {
    if (previousValue === updatedValue) return false;
    if (previousValue === null && updatedValue === null) return false;
    if (previousValue === undefined && updatedValue === undefined) return false;

    // Handle arrays
    if (Array.isArray(previousValue) && Array.isArray(updatedValue)) {
      return JSON.stringify(previousValue) !== JSON.stringify(updatedValue);
    }

    return true;
  };

  const renderUserDataField = (label, value, isChanged = false) => {
    const displayValue = (value === null || value === undefined || value === "") ? "" : value;

    return (
      <Box
        className={`${classes.fieldBox} ${isChanged ? classes.changedField : ""}`}
      >
        <Typography variant="body2" className={classes.fieldLabel}>
          {label}:{" "}
        </Typography>
        <Typography variant="body2" className={classes.fieldValue}>
          {displayValue}
        </Typography>
      </Box>
    );
  };

  const renderUserData = (userData, title, previousData = null, titleLink = null, userStatus = null) => {
    if (!userData) return null;

    return (
      <Box className={classes.userDataBox}>
        <Box className={classes.titleContainer}>
          <Typography variant="h6" className={classes.title}>
            {title}
          </Typography>
          {titleLink}
        </Box>
        {renderUserDataField("Username", userData.Username, previousData && hasFieldChanged(previousData.Username, userData.Username))}
        {renderUserDataField("First Name", userData.FirstName, previousData && hasFieldChanged(previousData.FirstName, userData.FirstName))}
        {renderUserDataField("Last Name", userData.LastName, previousData && hasFieldChanged(previousData.LastName, userData.LastName))}
        {renderUserDataField("Title", userData.Title, previousData && hasFieldChanged(previousData.Title, userData.Title))}
        {renderUserDataField("Email", userData.Email, previousData && hasFieldChanged(previousData.Email, userData.Email))}
        {renderUserDataField("Position", userData.Position, previousData && hasFieldChanged(previousData.Position, userData.Position))}
        {renderUserDataField("Phone Mobile", userData.PhoneMobile, previousData && hasFieldChanged(previousData.PhoneMobile, userData.PhoneMobile))}
        {renderUserDataField("Employee Number", userData.EmployeeNumber, previousData && hasFieldChanged(previousData.EmployeeNumber, userData.EmployeeNumber))}
        <Box
          className={`${classes.userRolesBox} ${previousData && hasFieldChanged(previousData.UserRoles, userData.UserRoles) ? classes.changedField : ""}`}
        >
          <Typography variant="body2" className={classes.fieldLabel}>
            User Roles:
          </Typography>
          <Typography
            variant="body2"
            className={`${classes.userRolesText} ${previousData && hasFieldChanged(previousData.UserRoles, userData.UserRoles) ? classes.expandedCell : ""}`}
          >
            {formatUserRoles(userData.UserRoles)}
          </Typography>
        </Box>
        {renderUserDataField(
          "Notification Preference",
          userData.NotificationPreference && userData.NotificationPreference.length > 0
            ? userData.NotificationPreference.join(", ")
            : "",
          previousData && hasFieldChanged(previousData.NotificationPreference, userData.NotificationPreference),
        )}
        {renderUserDataField(
          "Endorsement Types",
          userData.EndorsementTypes && userData.EndorsementTypes.length > 0
            ? formatEndorsementTypes(userData.EndorsementTypes)
            : "",
          previousData && hasFieldChanged(previousData.EndorsementTypes, userData.EndorsementTypes),
        )}
        {showSpecialtyProfiles && renderUserDataField(
          "Specialty Profiles",
          userData.UserSpecialtyProfiles && userData.UserSpecialtyProfiles.length > 0
            ? formatUserSpecialtyProfiles(userData.UserSpecialtyProfiles)
            : "",
          previousData && hasFieldChanged(previousData.UserSpecialtyProfiles, userData.UserSpecialtyProfiles),
        )}
        {userStatus && (
          <Box className={classes.changedField}>
            <Typography variant="body2" className={classes.fieldLabel}>
              Status:{" "}
            </Typography>
            <Typography variant="body2" className={classes.fieldValue}>
              {userStatus}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderUserDataComparison = (resultsJson, errorMessage) => {
    if (!resultsJson) return null;

    const { PreviousUserData, ImportedUserData, UpdatedUserData, UserReactivated } = resultsJson;

    // Check if there are any changes between previous and updated data
    const hasChanges = PreviousUserData && UpdatedUserData
      ? JSON.stringify(PreviousUserData) !== JSON.stringify(UpdatedUserData)
      : true;

    const resultingDataTitle = hasChanges ? "Resulting User Data" : "Resulting User Data (no changes)";

    return (
      <Box className={classes.comparisonContainer}>
        <Box className={classes.comparisonBoxes}>
          {renderUserData(ImportedUserData, "Imported User Data")}
          {PreviousUserData && renderUserData(PreviousUserData, "Original User Data", null, null, UserReactivated ? "Deactivated" : null)}
          {errorMessage ? (
            <Box className={classes.errorResultBox}>
              <Typography variant="h6" className={classes.errorResultTitle}>
                Result
              </Typography>
              <Typography variant="body2" color="error">
                {errorMessage}
              </Typography>
            </Box>
          ) : (
            UpdatedUserData && renderUserData(
              UpdatedUserData,
              resultingDataTitle,
              PreviousUserData,
              UpdatedUserData.UserId ? (
                <Link
                  to={{
                    pathname: `/admin/users/${UpdatedUserData.UserId}`,
                    state: { backUrl: location.pathname },
                  }}
                  className={classes.titleLink}
                >
                  (view user)
                </Link>
              ) : null,
              UserReactivated ? "Reactivated" : null,
            )
          )}
        </Box>
      </Box>
    );
  };

  const renderTableRow = detail => {
    const resultsJson = detail.resultsJson ? JSON.parse(detail.resultsJson) : null;
    const contributingRows = detail.otherContributingRows && detail.otherContributingRows.length > 0
      ? detail.otherContributingRows.join(", ")
      : "-";
    const isExpanded = expandedRow === detail.rowNumber;

    let errorCellClassName = "";
    if (isExpanded && detail.errorMessage) {
      errorCellClassName = classes.expandedErrorCell;
    } else if (isExpanded) {
      errorCellClassName = classes.expandedCell;
    }

    return (
      <>
        <TableRow
          key={detail.rowNumber || detail.id}
          onClick={() => handleRowClick(detail.rowNumber)}
          className={isExpanded ? classes.expandedRow : classes.normalRow}
        >
          <TableCell className={isExpanded ? classes.expandedCell : ""}>{detail.rowNumber}</TableCell>
          <TableCell className={isExpanded ? classes.expandedCell : ""}>{resultsJson?.ImportedUserData?.Username}</TableCell>
          <TableCell className={isExpanded ? classes.expandedCell : ""}>{detail.updateType}</TableCell>
          <TableCell className={isExpanded ? classes.expandedCell : ""}>{contributingRows}</TableCell>
          <TableCell className={errorCellClassName}>{detail.errorMessage}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className={classes.collapseCell} colSpan={5}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {renderUserDataComparison(resultsJson, detail.errorMessage)}
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };
  return (
    <PagingTable
      loading={isLoading}
      data={details}
      rowsPerPage={pageInfo.pageSize}
      columnSpan={5}
      pageSizeOptions={USER_IMPORT_LOG_PAGE_SIZE_OPTIONS}
      page={pageInfo.pageNumber - 1}
      count={pageInfo.totalRecords}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onChangePage={onChangePage}
      emptyMessage="No Import Log Details Found"
      tableHead={renderTableHeader}
      tableRow={item => renderTableRow(item)}
    />
  );
};

export default UserImportLogDetailsTable;
